import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  Pencil,
  Users,
  DollarSign,
  CheckCircle2,
  LayoutGrid,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Toaster, toast } from "sonner";
import { useUrlState } from "@/hooks/useUrlState";
import { useSessionState } from "@/hooks/useSessionState";
import {
  listUsers,
  getSubscriptionMetrics,
  updateUserSubscription,
} from "@/services/adminApi";
import type { AdminUserSubscription } from "@/services/adminApi";
import { ApiError } from "@/services/api";
import type { UsersUrlState, UsersSessionState } from "@/types/pageState";

// ─── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;
const DEFAULT_SERIAL_LOOKUP_MONTHLY_LIMIT = 1;

const TIERS = ["recon", "operator", "analyst"] as const;
const STATUSES = ["none", "active", "past_due", "canceled"] as const;

// ─── Query keys ────────────────────────────────────────────────────────────────

const userKeys = {
  all: ["admin", "users"] as const,
  list: (params: UsersUrlState) => [...userKeys.all, "list", params] as const,
  metrics: () => [...userKeys.all, "metrics"] as const,
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatMrr(mrr: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(mrr);
}

function tierVariant(
  tier: string,
): "default" | "secondary" | "outline" | "destructive" {
  switch (tier) {
    case "analyst":
      return "default";
    case "operator":
      return "secondary";
    default:
      return "outline";
  }
}

function statusVariant(
  status: string,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "active":
      return "default";
    case "past_due":
      return "destructive";
    case "canceled":
      return "secondary";
    default:
      return "outline";
  }
}

function parseSerialLookupMonthlyLimit(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;

  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function formatSerialLookupQuota(user: AdminUserSubscription): string {
  if (user.serial_lookup_unlimited) return "Unlimited";

  const limit = Number.isFinite(user.serial_lookup_monthly_limit)
    ? user.serial_lookup_monthly_limit
    : DEFAULT_SERIAL_LOOKUP_MONTHLY_LIMIT;

  return `${limit}/month`;
}

// ─── Page component ────────────────────────────────────────────────────────────

export default function Subscriptions() {
  const queryClient = useQueryClient();

  const [urlState, setUrlState] = useUrlState<UsersUrlState>({
    schema: {
      search: "string",
      tier: "string",
      status: "string",
      page: "number",
    },
    defaults: { page: 1 },
    replace: true,
    debounceMs: 300,
  });

  const [sessionState, setSessionState] = useSessionState<UsersSessionState>({
    key: "users-subscriptions-ui",
    defaultValue: { editUserId: null },
  });

  // Local form state for the edit dialog
  const [editTier, setEditTier] = useState<string>("");
  const [editStatus, setEditStatus] = useState<string>("");
  const [editSerialLookupMonthlyLimit, setEditSerialLookupMonthlyLimit] =
    useState<string>(String(DEFAULT_SERIAL_LOOKUP_MONTHLY_LIMIT));
  const [editSerialLookupUnlimited, setEditSerialLookupUnlimited] =
    useState<boolean>(false);

  // ─── Queries ────────────────────────────────────────────────────────────────

  const page = urlState.page ?? 1;
  const offset = (page - 1) * PAGE_SIZE;

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: userKeys.list(urlState),
    queryFn: () =>
      listUsers({
        search: urlState.search || undefined,
        tier: urlState.tier || undefined,
        status: urlState.status || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: userKeys.metrics(),
    queryFn: getSubscriptionMetrics,
  });

  const users = usersData?.users ?? [];
  const total = usersData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const parsedSerialLookupMonthlyLimit = parseSerialLookupMonthlyLimit(
    editSerialLookupMonthlyLimit,
  );
  const serialLookupLimitInvalid =
    !editSerialLookupUnlimited && parsedSerialLookupMonthlyLimit == null;

  // ─── Mutation ───────────────────────────────────────────────────────────────

  const editMutation = useMutation({
    mutationFn: () =>
      updateUserSubscription(sessionState.editUserId!, {
        subscription_tier: editTier || null,
        subscription_status: editStatus || null,
        serial_lookup_monthly_limit:
          parsedSerialLookupMonthlyLimit ?? DEFAULT_SERIAL_LOOKUP_MONTHLY_LIMIT,
        serial_lookup_unlimited: editSerialLookupUnlimited,
      }),
    onSuccess: (updated) => {
      toast.success(
        `Updated ${updated.email ?? `User #${updated.id}`} subscription.`,
      );
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      setSessionState((prev) => ({ ...prev, editUserId: null }));
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) {
        toast.warning(err.message);
      } else {
        toast.error(
          err instanceof Error ? err.message : "Failed to update subscription.",
        );
      }
    },
  });

  // ─── Dialog helpers ──────────────────────────────────────────────────────────

  function openEditDialog(user: AdminUserSubscription) {
    setEditTier(user.subscription_tier);
    setEditStatus(user.subscription_status);
    setEditSerialLookupMonthlyLimit(
      String(
        user.serial_lookup_monthly_limit ?? DEFAULT_SERIAL_LOOKUP_MONTHLY_LIMIT,
      ),
    );
    setEditSerialLookupUnlimited(Boolean(user.serial_lookup_unlimited));
    setSessionState((prev) => ({ ...prev, editUserId: user.id }));
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <Toaster position="top-right" theme="dark" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Subscriptions</h2>
        </div>

        {/* Metrics cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MetricCard
            icon={<Users className="size-4 text-muted-foreground" />}
            label="Total Users"
            value={metricsLoading ? "…" : String(metrics?.total_users ?? 0)}
          />
          <MetricCard
            icon={<CheckCircle2 className="size-4 text-muted-foreground" />}
            label="Active"
            value={metricsLoading ? "…" : String(metrics?.active_count ?? 0)}
          />
          <MetricCard
            icon={<DollarSign className="size-4 text-muted-foreground" />}
            label="Est. MRR"
            value={
              metricsLoading ? "…" : formatMrr(metrics?.estimated_mrr ?? 0)
            }
          />
          <MetricCard
            icon={<LayoutGrid className="size-4 text-muted-foreground" />}
            label="By Tier"
            value={
              metricsLoading || !metrics
                ? "…"
                : Object.entries(metrics.by_tier)
                    .filter(([, n]) => n > 0)
                    .map(([t, n]) => `${t}: ${n}`)
                    .join(" · ") || "—"
            }
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="sub-search" className="text-xs">
              Search
            </Label>
            <Input
              id="sub-search"
              placeholder="Email or name…"
              className="h-8 w-60 text-sm"
              value={urlState.search ?? ""}
              onChange={(e) =>
                setUrlState((prev) => ({
                  ...prev,
                  search: e.target.value || undefined,
                  page: 1,
                }))
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Tier</Label>
            <Select
              value={urlState.tier ?? "__all__"}
              onValueChange={(v) =>
                setUrlState((prev) => ({
                  ...prev,
                  tier: v === "__all__" ? undefined : v,
                  page: 1,
                }))
              }
            >
              <SelectTrigger className="h-8 w-36 text-sm">
                <SelectValue placeholder="All tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All tiers</SelectItem>
                {TIERS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select
              value={urlState.status ?? "__all__"}
              onValueChange={(v) =>
                setUrlState((prev) => ({
                  ...prev,
                  status: v === "__all__" ? undefined : v,
                  page: 1,
                }))
              }
            >
              <SelectTrigger className="h-8 w-36 text-sm">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(urlState.search || urlState.tier || urlState.status) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 self-end"
              onClick={() =>
                setUrlState((prev) => ({
                  ...prev,
                  search: undefined,
                  tier: undefined,
                  status: undefined,
                  page: 1,
                }))
              }
            >
              Clear filters
            </Button>
          )}
        </div>

        {/* Table */}
        {usersLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="size-5 animate-spin" />
            Loading…
          </div>
        ) : users.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8">
            No users found.
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Serial Lookups</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="font-medium text-sm">
                        {user.name ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {user.email ?? `#${user.id}`}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={tierVariant(user.subscription_tier)}>
                        {user.subscription_tier}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(user.subscription_status)}>
                        {user.subscription_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          user.serial_lookup_unlimited ? "default" : "outline"
                        }
                      >
                        {formatSerialLookupQuota(user)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(user.subscription_started_at)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(user.subscription_expires_at)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(user.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(user)}
                        title="Edit subscription"
                      >
                        <Pencil className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() =>
                  setUrlState((prev) => ({ ...prev, page: page - 1 }))
                }
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() =>
                  setUrlState((prev) => ({ ...prev, page: page + 1 }))
                }
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit subscription dialog */}
      <Dialog
        open={sessionState.editUserId != null}
        onOpenChange={(open) => {
          if (!open) setSessionState((prev) => ({ ...prev, editUserId: null }));
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Subscription</DialogTitle>
            <DialogDescription>
              {sessionState.editUserId != null
                ? (() => {
                    const u = users.find(
                      (x) => x.id === sessionState.editUserId,
                    );
                    return u
                      ? (u.email ?? `User #${u.id}`)
                      : `User #${sessionState.editUserId}`;
                  })()
                : "Update tier, status, and serial lookup allowance."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Tier</Label>
              <Select value={editTier} onValueChange={setEditTier}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <Label htmlFor="serial-lookup-unlimited">
                    Unlimited serial lookups
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Bypasses the monthly cap for users whose tier can access
                    serial lookup.
                  </p>
                </div>
                <Switch
                  id="serial-lookup-unlimited"
                  checked={editSerialLookupUnlimited}
                  onCheckedChange={setEditSerialLookupUnlimited}
                  aria-label="Toggle unlimited serial lookups"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="serial-lookup-monthly-limit">
                  Monthly finite limit
                </Label>
                <Input
                  id="serial-lookup-monthly-limit"
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={editSerialLookupMonthlyLimit}
                  disabled={editSerialLookupUnlimited}
                  aria-invalid={serialLookupLimitInvalid}
                  onChange={(e) =>
                    setEditSerialLookupMonthlyLimit(e.target.value)
                  }
                />
                {serialLookupLimitInvalid ? (
                  <p className="text-xs text-destructive">
                    Enter a non-negative whole number.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Use 0 to block monthly serial lookups without granting
                    unlimited access.
                  </p>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Changes are applied to the local database only. No Stripe calls
              are made.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({ ...prev, editUserId: null }))
              }
            >
              Cancel
            </Button>
            <Button
              disabled={
                !editTier ||
                !editStatus ||
                serialLookupLimitInvalid ||
                editMutation.isPending
              }
              onClick={() => editMutation.mutate()}
            >
              {editMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Metric card ───────────────────────────────────────────────────────────────

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function MetricCard({ icon, label, value }: MetricCardProps) {
  return (
    <Card>
      <CardHeader className="pb-1 pt-4 px-4">
        <CardTitle className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="text-2xl font-semibold truncate">{value}</div>
      </CardContent>
    </Card>
  );
}
